/**
 * 子步骤记录 / 单证 API（对齐 SubstepController）。
 *
 * 真实端点（经网关 /api，StripPrefix=1）：
 * - GET  /container/substeps/config/{nodeCode}  节点子步骤配置（含 form_template_id，动态表单数据源）
 * - POST /container/substeps/records/init          按节点初始化子步骤（幂等）
 * - PUT  /container/substeps/records/{id}           按 id 更新状态/数据
 * - GET  /container/substeps/records                读取节点全部子步骤
 * - GET  /container/substeps/check-complete/detail  推进前预校验（required 是否全完成）
 * - POST /container/substeps/documents              子步骤级证据上传（落 node_documents）
 *
 * 约定：必须先 init 生成 pending 记录，再 update 置 completed；required 子步骤未全完成，
 * advance 会被 SubstepGuard 硬阻断（HTTP 非 200，message 含原因）。
 */
import { request } from './request';
import type { SubstepRecord, SubstepCompleteDetail } from './types';

/** 子步骤配置（对齐 NodeSubstep 实体；GET /container/substeps/config/{nodeCode}） */
export interface SubstepConfig {
  id: string;
  nodeCode?: string;
  substepCode: string;
  substepName?: string;
  substepSeq?: number;
  executionMode?: string;
  formTemplateId?: string;
  actionType?: string;
  isRequired?: boolean;
  isActive?: boolean;
  description?: string;
  [key: string]: unknown;
}

/** 按节点取子步骤配置（含 form_template_id，动态表单渲染数据源）；传 orderId 时按品类过滤 */
export function getSubstepConfig(nodeCode: string, orderId?: string): Promise<SubstepConfig[]> {
  return request<SubstepConfig[]>(`/container/substeps/config/${nodeCode}`, { params: orderId ? { orderId } : undefined });
}

export interface SubstepInitPayload {
  orderId: string;
  nodeCode: string;
}

export function initSubsteps(payload: SubstepInitPayload): Promise<unknown> {
  return request('/container/substeps/records/init', { method: 'POST', body: payload });
}

export function updateSubstepRecord(
  id: string,
  status: string,
  data?: Record<string, unknown>,
): Promise<unknown> {
  return request(`/container/substeps/records/${id}`, {
    method: 'PUT',
    body: { status, data },
  });
}

/**
 * 更新子步骤记录（对齐后台：data 传 JSON 字符串，后端 record.data 存 string）。
 * 后台 NodeSubstepPanel 用 JSON.stringify(formValues)，与后端 SubstepRequirementService
 * 的表单必填字段判定口径一致（读 record.data 解析 JSON）。
 */
export function updateSubstepRecordWithData(
  id: string,
  status: string,
  dataJson?: string,
): Promise<unknown> {
  return request(`/container/substeps/records/${id}`, {
    method: 'PUT',
    body: { status, data: dataJson },
  });
}

export function getSubstepRecords(orderId: string, nodeCode: string): Promise<SubstepRecord[]> {
  return request<SubstepRecord[]>('/container/substeps/records', { params: { orderId, nodeCode } });
}

export function checkSubstepComplete(
  orderId: string,
  nodeCode: string,
): Promise<SubstepCompleteDetail> {
  return request<SubstepCompleteDetail>('/container/substeps/check-complete/detail', {
    params: { orderId, nodeCode },
  });
}

export interface SubstepDocumentPayload {
  orderId: string;
  nodeCode: string;
  substepCode: string;
  docType: string;
  fileId: string;
  /** 多检查点单据（③）所属检查点 code，缺省为普通单次单据 */
  checkpointCode?: string;
  docNo?: string;
}

export function uploadSubstepDocument(payload: SubstepDocumentPayload): Promise<unknown> {
  return request('/container/substeps/documents', { method: 'POST', body: payload });
}

/** 删除子步骤单据（③ 多检查点照片移除，按 node_documents.id） */
export function deleteSubstepDocument(id: string): Promise<unknown> {
  return request(`/container/substeps/documents/${id}`, { method: 'DELETE' });
}

/** 子步骤单据（对齐 NodeDocument；GET /container/substeps/documents 返回） */
export interface SubstepDocument {
  id: string;
  orderId?: string;
  substepId?: string;
  nodeRecordId?: string;
  docType?: string;
  fileId?: string;
  docNo?: string;
  /** 多检查点单据归属（③）：该照片所属检查点 code */
  checkpointCode?: string;
  createdAt?: string;
  [key: string]: unknown;
}

/** 查询订单已上传的子步骤单据（substepCode 前端过滤；后端仅按 orderId/docType 查询） */
export async function getSubstepDocuments(
  orderId: string,
  substepCode?: string,
  docType?: string,
): Promise<SubstepDocument[]> {
  const list = await request<SubstepDocument[]>('/container/substeps/documents', {
    params: { orderId, docType },
  });
  const arr = Array.isArray(list) ? list : [];
  if (!substepCode) return arr;
  return arr.filter((d) => d.substepId === substepCode);
}

/** 节点单据规则（对齐 NodeDocRuleItem；GET /container/nodeRules/{nodeCode}） */
export interface NodeDocRuleItem {
  id: string;
  nodeCode: string;
  substepCode?: string;
  docType: string;
  required?: boolean;
  allowMultiple?: boolean;
  buyerAccessible?: boolean;
  reuseScope?: 'NODE' | 'ORDER';
  description?: string;
  /** 多检查点配置（③）：NULL/空=无检查点；元素 {code,label,min_count} */
  checkpoints?: string | { code: string; label: string; required?: boolean; min_count: number }[];
  [key: string]: unknown;
}

/** 获取节点要求的附件规则（处理弹窗按 substepCode 过滤） */
export function getNodeDocRules(nodeCode: string, orderId?: string): Promise<NodeDocRuleItem[]> {
  return request<NodeDocRuleItem[]>(`/container/nodeRules/${nodeCode}`, {
    params: orderId ? { orderId } : undefined,
  });
}

/** 检查节点所有子步骤是否完成（处理完成后触发 onComplete 回调） */
export function checkNodeComplete(orderId: string, nodeCode: string): Promise<boolean> {
  return request<boolean>('/container/substeps/check-complete', { params: { orderId, nodeCode } });
}
